import { Controller } from "@hotwired/stimulus"
import { getAllJSON, errorDetail } from "../api.js"

// Kontroler personelu: akordeon zapotrzebowania (widok planowania popytu).
//
// connect(): pobiera agregat i renderuje wiersze akordeonowe (kategoria × województwo).
//            Równolegle populuje selecty filtrów z URL values (F4).
//
// toggle(): pierwsze rozwinięcie → pobiera liście (deklaracje org.) z API z filtrem
//           status=active (F6) i cache'uje; kolejne rozwinięcia tej samej grupy → odczyt
//           z cache, brak dodatkowych żądań. Zwinięcie → hidden=true, cache zostaje.
//
// applyFilters(): odczytuje aktualne wartości filtrów, pobiera nowy agregat z serwera
//                 (NIE filtruje wyrenderowanych wierszy — liczby MUSZĄ pochodzić z bazy).
//                 Czyści cache liści, bo zmiana filtrów może zmienić skład grup.
//
// Rozróżnienie "pusta odpowiedź" vs "błąd pobierania" (e0083):
//   - 0 deklaracji w grupie: leafStatus = "Brak deklaracji w tej grupie."
//   - fetch error: leafStatus = "Nie udało się załadować deklaracji."
//   Obie wyglądają identycznie dla planisty bez tego rozróżnienia.
//
// Dlaczego lazy (nie eager) liście:
//   Agregat zwraca county_breakdown {county, county_name, total} — nie ma nazw organizacji.
//   Nazwy org. są dostępne tylko przez /api/staff/demand-orders/?category=X&voivodeship=Y.
//   Lazy-on-expand kosztuje tyle samo co eager, ale utrzymuje initial load jako jedno żądanie.
//
// Dlaczego status=active w fetch liści (F6):
//   Agregat sumuje wyłącznie aktywne zamówienia. Pokazywanie anulowanych pod nagłówkiem
//   który ich nie liczy dawało planiście wiersze nie sumujące się do liczby nad nimi.
//   Filtr status=active wyrównuje widok liści z nagłówkiem grupy.
//
// Zasady:
//   - Zero innerHTML — wszystkie węzły przez createElement + textContent.
//   - getAllJSON/errorDetail z ../api.js — nie raw fetch.
//   - this.dispatch() bubbles do window z definicji — brak ręcznego window.dispatchEvent.

export default class extends Controller {
  static targets = [
    "accordion",
    "status",
    "verticalFilter",
    "categoryFilter",
    "voivFilter",
    "countyFilter",
    "statusFilter",
    "applyBtn",
  ]

  static values = {
    aggregateUrl: String,
    ordersUrl: String,
    verticalsUrl: String,
    categoriesUrl: String,
    voivodeshipsUrl: String,
    countiesUrl: String,
  }

  // Cache liści: "category:voivodeship" → orders[] | "error"
  // Klucz jest stringiem złożonym z PK kategorii i województwa.
  #leafCache = new Map()

  async connect() {
    this.#showPageStatus("Ładowanie danych…")
    // Nasłuch kaskady województwo → powiat — przed awaitami, żeby nie czekać na fetch.
    if (this.hasVoivFilterTarget) {
      this.voivFilterTarget.addEventListener("change", () => this.#onVoivFilterChange())
    }
    // Równoległy fetch: opcje filtrów + agregat (niezależne zasoby).
    await Promise.all([
      this.#loadFilterOptions(),
      this.#loadAggregate(),
    ])
  }

  // Akcja rozwinięcia/zwinięcia grupy akordeonowej.
  // currentTarget = przycisk toggle (data-action="demand-staff#toggle").
  // Liście ładowane przy pierwszym rozwinięciu (lazy); cache blokuje ponowne żądania.
  async toggle({ currentTarget }) {
    const toggleEl = currentTarget
    const groupEl = toggleEl.closest('[data-demand-staff-target="group"]')
    if (!groupEl) return

    const leavesEl = groupEl.querySelector('[data-demand-staff-target="leaves"]')
    if (!leavesEl) return

    const isOpen = toggleEl.classList.contains("is-open")

    if (isOpen) {
      // Zamknij — dane w cache, panel ukryty
      toggleEl.classList.remove("is-open")
      toggleEl.setAttribute("aria-expanded", "false") // F11
      leavesEl.hidden = true
      return
    }

    // Otwórz — panel od razu widoczny (zanim fetch się skończy)
    toggleEl.classList.add("is-open")
    toggleEl.setAttribute("aria-expanded", "true") // F11
    leavesEl.hidden = false

    const key = toggleEl.dataset.groupKey
    if (!key) return

    if (this.#leafCache.has(key)) {
      const cached = this.#leafCache.get(key)
      if (cached === "error") {
        this.#showLeafError(groupEl)
      } else {
        this.#renderLeaves(groupEl, cached)
      }
      return
    }

    // Pierwsze rozwinięcie: pokaż loading, pobierz liście
    this.#showLeafLoading(groupEl)
    const [catId, voivId] = key.split(":")

    let orders
    try {
      // status=active: agregat liczy tylko aktywne — liście muszą pasować (F6).
      orders = await getAllJSON(
        `${this.ordersUrlValue}?category=${catId}&voivodeship=${voivId}&status=active`,
      )
    } catch {
      this.#leafCache.set(key, "error")
      this.#showLeafError(groupEl)
      return
    }

    this.#leafCache.set(key, orders)
    this.#renderLeaves(groupEl, orders)
  }

  // Akcja zastosowania filtrów.
  // Pobiera agregat z parametrami filtrów — NIE filtruje wyrenderowanych wierszy.
  // Czyści cache liści, bo zmiana filtrów zmienia skład i liczby grup.
  async applyFilters() {
    this.#leafCache.clear()
    this.#showPageStatus("Ładowanie danych…")
    await this.#loadAggregate()
  }

  // --- private ---

  async #loadAggregate() {
    const params = this.#filterParams()
    const url = params ? `${this.aggregateUrlValue}?${params}` : this.aggregateUrlValue

    let groups
    try {
      groups = await getAllJSON(url)
    } catch {
      this.accordionTarget.textContent = ""
      this.#showPageStatus("Nie udało się załadować danych. Spróbuj ponownie.")
      return
    }

    this.accordionTarget.textContent = ""

    if (!groups.length) {
      this.#showPageStatus("Brak deklaracji spełniających kryteria.")
      return
    }

    this.statusTarget.hidden = true

    const tpl = document.getElementById("demand-staff-group-template")
    for (const g of groups) {
      const row = tpl.content.cloneNode(true).firstElementChild

      // Klucz grupy przechowywany na przycisku toggle — identyfikuje cache przy re-expand.
      const key = `${g.category}:${g.voivodeship}`
      const toggleEl = row.querySelector('[data-demand-staff-target="toggle"]')
      if (toggleEl) {
        toggleEl.dataset.groupKey = key
        toggleEl.setAttribute("aria-expanded", "false") // F11: ekran czytnika zna stan
      }

      row.querySelector('[data-field="category"]').textContent =
        g.category_name ?? String(g.category)
      row.querySelector('[data-field="voivodeship"]').textContent =
        g.voivodeship_name ?? String(g.voivodeship)
      // F1: pole API to `total`, nie `total_quantity`.
      row.querySelector('[data-field="total"]').textContent = String(g.total)

      this.accordionTarget.appendChild(row)
    }
  }

  // Pobiera i populuje selecty filtrów ze słownikowych URL values (F4).
  // Brak oczekiwania na te żądania nie blokuje głównego akordeonowego load — błąd fetch
  // jest cichy (select zostaje z opcjami z szablonu). Kaskada powiatów obsługiwana osobno.
  async #loadFilterOptions() {
    await Promise.all([
      this.hasVerticalFilterTarget
        ? this.#populateSelect(this.verticalFilterTarget, this.verticalsUrlValue)
        : Promise.resolve(),
      this.hasCategoryFilterTarget
        ? this.#populateSelect(this.categoryFilterTarget, this.categoriesUrlValue)
        : Promise.resolve(),
      this.hasVoivFilterTarget
        ? this.#populateSelect(this.voivFilterTarget, this.voivodeshipsUrlValue)
        : Promise.resolve(),
    ])
  }

  // Wypełnia select opcjami z API; zachowuje pustą opcję "Wszystkie" (value="").
  // Błąd fetch → cichy powrót, istniejące opcje w selekcie nie są ruszane.
  async #populateSelect(selectEl, url) {
    if (!url) return
    let items
    try {
      items = await getAllJSON(url)
    } catch {
      return
    }
    const blank = selectEl.querySelector('option[value=""]')
    selectEl.textContent = ""
    if (blank) selectEl.appendChild(blank)
    for (const item of items) {
      const opt = document.createElement("option")
      opt.value = String(item.id)
      opt.textContent = item.name
      selectEl.appendChild(opt)
    }
  }

  // Kaskada: zmiana województwa w filtrze → załaduj powiaty dla tego województwa.
  // Wyczyszczenie wyboru województwa → wyczyść powiaty i zablokuj select.
  async #onVoivFilterChange() {
    if (!this.hasCountyFilterTarget) return
    const voivId = this.voivFilterTarget.value
    this.countyFilterTarget.textContent = ""
    if (!voivId) {
      this.countyFilterTarget.disabled = true
      return
    }
    let counties
    try {
      counties = await getAllJSON(`${this.countiesUrlValue}?voivodeship=${voivId}`)
    } catch {
      return
    }
    for (const c of counties) {
      const opt = document.createElement("option")
      opt.value = String(c.id)
      opt.textContent = c.name
      this.countyFilterTarget.appendChild(opt)
    }
    this.countyFilterTarget.disabled = false // F4: usuwa hard-coded disabled z szablonu
  }

  // Buduje query-string z aktywnych filtrów; zwraca "" gdy brak filtrów.
  #filterParams() {
    const parts = []
    if (this.hasVerticalFilterTarget && this.verticalFilterTarget.value) {
      parts.push(`vertical=${encodeURIComponent(this.verticalFilterTarget.value)}`)
    }
    if (this.hasCategoryFilterTarget && this.categoryFilterTarget.value) {
      parts.push(`category=${encodeURIComponent(this.categoryFilterTarget.value)}`)
    }
    if (this.hasVoivFilterTarget && this.voivFilterTarget.value) {
      parts.push(`voivodeship=${encodeURIComponent(this.voivFilterTarget.value)}`)
    }
    if (this.hasCountyFilterTarget && this.countyFilterTarget.value) {
      parts.push(`county=${encodeURIComponent(this.countyFilterTarget.value)}`)
    }
    if (this.hasStatusFilterTarget && this.statusFilterTarget.value) {
      parts.push(`status=${encodeURIComponent(this.statusFilterTarget.value)}`)
    }
    return parts.join("&")
  }

  #showPageStatus(message) {
    this.statusTarget.hidden = false
    this.statusTarget.textContent = message
  }

  // Pokazuje "Ładowanie…" w panelu liści przed fetch.
  #showLeafLoading(groupEl) {
    const leafStatusEl = groupEl.querySelector('[data-demand-staff-target="leafStatus"]')
    const leafRowsEl = groupEl.querySelector('[data-demand-staff-target="leafRows"]')
    if (leafRowsEl) leafRowsEl.textContent = ""
    if (leafStatusEl) {
      leafStatusEl.hidden = false
      leafStatusEl.textContent = "Ładowanie deklaracji…"
    }
  }

  // Pokazuje błąd w panelu liści po nieudanym fetch.
  // Celowo inny tekst niż "Brak deklaracji" — e0083: nie wolno mylić błędu z pustą listą.
  #showLeafError(groupEl) {
    const leafStatusEl = groupEl.querySelector('[data-demand-staff-target="leafStatus"]')
    const leafRowsEl = groupEl.querySelector('[data-demand-staff-target="leafRows"]')
    if (leafRowsEl) leafRowsEl.textContent = ""
    if (leafStatusEl) {
      leafStatusEl.hidden = false
      leafStatusEl.textContent = "Nie udało się załadować deklaracji."
    }
  }

  // Renderuje wiersze liści lub komunikat "Brak deklaracji".
  // county_names (StaffDemandOrderSerializer) zawiera nazwy czytelne; counties to PK-i (F5).
  #renderLeaves(groupEl, orders) {
    const leafStatusEl = groupEl.querySelector('[data-demand-staff-target="leafStatus"]')
    const leafRowsEl = groupEl.querySelector('[data-demand-staff-target="leafRows"]')
    if (!leafRowsEl) return

    leafRowsEl.textContent = ""

    if (!orders.length) {
      if (leafStatusEl) {
        leafStatusEl.hidden = false
        leafStatusEl.textContent = "Brak deklaracji w tej grupie."
      }
      return
    }

    if (leafStatusEl) leafStatusEl.hidden = true

    const tpl = document.getElementById("demand-staff-leaf-template")
    for (const order of orders) {
      const row = tpl.content.cloneNode(true).firstElementChild

      row.querySelector('[data-field="organization"]').textContent =
        order.organization_name ?? String(order.organization)

      const countiesEl = row.querySelector('[data-field="counties"]')
      if (!order.counties?.length) {
        countiesEl.textContent = "całe województwo"
      } else {
        // F5: county_names to nazwy z StaffDemandOrderSerializer; counties to PK-i.
        countiesEl.textContent =
          order.county_names?.join(", ") ?? order.counties.join(", ")
      }

      row.querySelector('[data-field="quantity"]').textContent = String(order.monthly_quantity)

      const notesEl = row.querySelector('[data-field="notes"]')
      notesEl.textContent = order.notes ?? ""

      leafRowsEl.appendChild(row)
    }
  }
}
